/**
 * assets/cesium-integration.js — CESIUM-LANDSD-BRIDGE-03
 * Owner: Shared 3D Cesium Runtime & LandsD 3D Tiles Integration
 *
 * Owns:
 * - Cesium Viewer lifecycle for sections/cesium-3d-map.liquid.
 * - Canonical camera controls exposed through [data-cesium-action].
 * - Section-local status projection and safe Shopify section re-initialization.
 */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-module="cesium-3d-map"]';
  const CANVAS_SELECTOR = '[data-cesium-canvas]';
  const ACTION_SELECTOR = '[data-cesium-action]';
  const STATUS_SELECTOR = '[data-cesium-status]';
  const FALLBACK_VIEW = Object.freeze({
    lon: 114.1581,
    lat: 22.2819,
    height: 450,
    heading: 0,
    pitch: -35,
    roll: 0
  });

  function finite(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function configView(root) {
    const config = window.CesiumConfig || window.GCityCesiumConfig || {};
    const scene = root && root.getAttribute('data-scene');
    const candidate = (config.locations && scene && config.locations[scene]) || config.defaultView || FALLBACK_VIEW;
    return {
      lon: finite(candidate.lon, FALLBACK_VIEW.lon),
      lat: finite(candidate.lat, FALLBACK_VIEW.lat),
      height: finite(candidate.height, FALLBACK_VIEW.height),
      heading: finite(candidate.heading, FALLBACK_VIEW.heading),
      pitch: finite(candidate.pitch, FALLBACK_VIEW.pitch),
      roll: finite(candidate.roll, FALLBACK_VIEW.roll)
    };
  }

  function animationDuration() {
    const config = window.CesiumConfig || window.GCityCesiumConfig || {};
    return Math.max(0, finite(config.animation && config.animation.duration, 1.8));
  }

  class GCityCesiumBridge {
    constructor(containerEl, options = {}) {
      this.container = containerEl;
      this.root = options.root || containerEl.closest(ROOT_SELECTOR);
      this.options = options;
      this.viewer = null;
      this.tileset = null;
      this.isInitialized = false;
      this.initialView = configView(this.root);
      this.boundClick = this.handleControlClick.bind(this);

      this.bindControls();
      this.init();
    }

    message(name, fallback) {
      if (!this.root) return fallback || '';
      return this.root.getAttribute(`data-cesium-message-${name}`) || fallback || '';
    }

    setStatus(state, message) {
      if (!this.root) return;
      const status = this.root.querySelector(STATUS_SELECTOR);
      if (!status) return;
      status.setAttribute('data-state', state || 'ready');
      if (message) status.textContent = message;
    }

    bindControls() {
      if (!this.root || this.root.__gcityCesiumControlsBound) return;
      this.root.__gcityCesiumControlsBound = true;
      this.root.addEventListener('click', this.boundClick);
    }

    handleControlClick(event) {
      const button = event.target && event.target.closest ? event.target.closest(ACTION_SELECTOR) : null;
      if (!button || !this.root || !this.root.contains(button)) return;
      event.preventDefault();
      this.applyCameraAction(button.getAttribute('data-cesium-action'));
    }

    applyInitialView(mode) {
      if (!this.viewer || !this.viewer.camera || typeof Cesium === 'undefined') return false;
      const view = this.initialView;
      const destination = Cesium.Cartesian3.fromDegrees(view.lon, view.lat, view.height);
      const orientation = {
        heading: Cesium.Math.toRadians(view.heading),
        pitch: Cesium.Math.toRadians(view.pitch),
        roll: Cesium.Math.toRadians(view.roll)
      };

      if (mode === 'fly' && typeof this.viewer.camera.flyTo === 'function') {
        this.viewer.camera.flyTo({
          destination,
          orientation,
          duration: animationDuration()
        });
      } else {
        this.viewer.camera.setView({ destination, orientation });
      }
      return true;
    }

    applyCameraAction(action) {
      if (!action) return;
      if (!this.viewer || !this.viewer.camera || typeof Cesium === 'undefined') {
        this.setStatus('preview', this.message('action-preview', '3D spatial navigation action preview'));
        return;
      }

      const camera = this.viewer.camera;
      if (action === 'pitch') {
        camera.lookDown(Cesium.Math.toRadians(10));
      } else if (action === 'rotate') {
        camera.lookRight(Cesium.Math.toRadians(15));
      } else if (action === 'reset') {
        this.applyInitialView('fly');
      } else {
        return;
      }

      const applied = this.message('action-applied', '3D spatial navigation action applied');
      this.setStatus('ready', `${applied}: ${action}`);
      try {
        this.root.dispatchEvent(new CustomEvent('gcity:cesium:action', {
          bubbles: true,
          detail: { action, bridge: this }
        }));
      } catch (_error) {}
    }

    async init() {
      if (!this.container || this.isInitialized) return;

      if (typeof Cesium === 'undefined') {
        this.setStatus('fallback', this.message('renderer-unavailable', 'Interactive 3D renderer is unavailable.'));
        console.warn('[GCity 3D] Cesium library is not loaded.');
        return;
      }

      try {
        const config = window.CesiumConfig || window.GCityCesiumConfig || {};
        if (config.ionAccessToken && Cesium.Ion) Cesium.Ion.defaultAccessToken = config.ionAccessToken;

        const viewerOptions = {
          animation: false,
          timeline: false,
          navigationHelpButton: false,
          baseLayerPicker: false,
          fullscreenButton: false,
          geocoder: false,
          homeButton: false,
          sceneModePicker: false,
          shadows: true,
          shouldAnimate: true
        };
        if (typeof Cesium.createWorldTerrain === 'function') {
          viewerOptions.terrainProvider = Cesium.createWorldTerrain();
        }

        this.viewer = new Cesium.Viewer(this.container, viewerOptions);

        if (this.viewer._cesiumWidget && this.viewer._cesiumWidget._creditContainer) {
          this.viewer._cesiumWidget._creditContainer.style.display = 'none';
        }

        this.applyInitialView('set');
        this.isInitialized = true;
        this.setStatus('ready', this.message('map-ready', 'Cesium 3D spatial engine ready.'));

        try {
          await this.loadLandsDTiles();
          this.setStatus('ready', this.message('city-ready', 'Real-scene 3D digital twin scene ready.'));
        } catch (error) {
          console.warn('[GCity 3D] Scene data could not be fully loaded.', error);
        }

        document.dispatchEvent(new CustomEvent('gcity:cesium:ready', {
          detail: { viewer: this.viewer, bridge: this }
        }));
      } catch (error) {
        this.setStatus('error', this.message('viewer-failed', '3D spatial viewer initialization failed.'));
        console.error('[GCity 3D] Initialization failed:', error);
      }
    }

    async loadLandsDTiles() {
      if (!this.viewer || typeof Cesium === 'undefined') return;
      const proxyEndpoint = this.options.tilesProxyEndpoint || '/api/spatial/v1/landsd/3d-tiles';

      try {
        this.tileset = await Cesium.Cesium3DTileset.fromUrl(proxyEndpoint, {
          maximumScreenSpaceError: 16,
          shadows: Cesium.ShadowMode.ENABLED
        });
        this.viewer.scene.primitives.add(this.tileset);
      } catch (error) {
        console.warn('[GCity 3D] LandsD 3D Tiles proxy unavailable, using fallback OSM buildings.', error);
        if (typeof Cesium.createOsmBuildingsAsync !== 'function') return;
        this.tileset = await Cesium.createOsmBuildingsAsync();
        this.viewer.scene.primitives.add(this.tileset);
      }
    }

    setShadowTime(julianDate) {
      if (this.viewer && this.viewer.clock) {
        this.viewer.clock.currentTime = julianDate;
      }
    }
  }

  function rootsWithin(scope) {
    const roots = [];
    if (!scope) return roots;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    return roots;
  }

  function boot(scope) {
    rootsWithin(scope || document).forEach((root) => {
      if (root.__gcityCesiumBridge) return;
      const container = root.querySelector(CANVAS_SELECTOR);
      if (!container) {
        const status = root.querySelector(STATUS_SELECTOR);
        if (status) {
          status.setAttribute('data-state', 'error');
          status.textContent = root.getAttribute('data-cesium-message-canvas-unavailable') || '3D spatial map canvas is unavailable.';
        }
        return;
      }
      root.__gcityCesiumBridge = new GCityCesiumBridge(container, { root });
    });
  }

  window.GCity = window.GCity || {};
  window.GCity.CesiumBridge = GCityCesiumBridge;
  window.GCity.bootCesium = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
